
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ClientEmailAnalytics {
  clientId: string;
  clientName: string;
  today: number;
  yesterday: number;
  last7Days: number;
  last14Days: number;
  last30Days: number;
  todayReplies: number;
  yesterdayReplies: number;
  last7DaysReplies: number;
  last14DaysReplies: number;
  last30DaysReplies: number;
  todayBounces: number;
  yesterdayBounces: number;
  last7DaysBounces: number;
  last14DaysBounces: number;
  last30DaysBounces: number;
}

// Fallback client names for backwards compatibility (will be overridden by database lookup)
const FALLBACK_CLIENT_NAMES: Record<string, string> = {
  '5559': 'Yotpo',
  '8241': 'Loop',
  '167887': 'Cruva',
  '1756553006828': 'Outboundbuddy',
};

export const useClientSpecificEmailAnalytics = (daysBack: number = 30) => {
  return useQuery({
    queryKey: ['clientSpecificEmailAnalytics', daysBack],
    queryFn: async (): Promise<ClientEmailAnalytics[]> => {
      console.log('📊 [useClientSpecificEmailAnalytics] Fetching client-specific email analytics using database function...');
      
      // Call the new database function that matches the main analytics logic
      const { data, error } = await supabase.rpc('get_client_email_analytics', {
        days_back: daysBack
      });

      if (error) {
        console.error('❌ [useClientSpecificEmailAnalytics] Error calling database function:', error);
        throw error;
      }

      console.log('📊 [useClientSpecificEmailAnalytics] Raw database response:', data);

      // Transform the database response to match our interface
      const clients = (data as any)?.clients || [];
      
      // Extract unique client IDs from analytics data
      const clientIds = [...new Set(clients.map((c: any) => c.client_id?.toString()).filter(Boolean))];
      
      // Fetch client names from database dynamically
      // Note: client_id is a numeric column (bigint) in the clients table, not the UUID id column
      let clientNameMap: Record<string, string> = { ...FALLBACK_CLIENT_NAMES };
      
      if (clientIds.length > 0) {
        try {
          // Convert string client IDs to integers for the database query
          // client_id is numeric, so we use parseInt and filter out NaN values
          const numericClientIds = clientIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
          
          const { data: clientsData, error: clientsError } = await supabase
            .from('clients')
            .select('client_id, name')
            .in('client_id', numericClientIds);
          
          if (!clientsError && clientsData) {
            // Build dynamic mapping from database
            clientsData.forEach((client: any) => {
              if (client.client_id && client.name) {
                clientNameMap[client.client_id.toString()] = client.name;
              }
            });
            console.log('📊 [useClientSpecificEmailAnalytics] Fetched client names from database:', clientNameMap);
          }
        } catch (err) {
          console.warn('⚠️ [useClientSpecificEmailAnalytics] Failed to fetch client names from database, using fallback:', err);
        }
      }
      
      const clientAnalytics: ClientEmailAnalytics[] = clients.map((client: any) => ({
        clientId: client.client_id?.toString() || '',
        clientName: clientNameMap[client.client_id?.toString()] || `Client ${client.client_id}`,
        today: client.today || 0,
        yesterday: client.yesterday || 0,
        last7Days: client.last_7_days || 0,
        last14Days: client.last_14_days || 0,
        last30Days: client.last_30_days || 0,
        todayReplies: client.today_replies || 0,
        yesterdayReplies: client.yesterday_replies || 0,
        last7DaysReplies: client.last_7_days_replies || 0,
        last14DaysReplies: client.last_14_days_replies || 0,
        last30DaysReplies: client.last_30_days_replies || 0,
        todayBounces: client.today_bounces || 0,
        yesterdayBounces: client.yesterday_bounces || 0,
        last7DaysBounces: client.last_7_days_bounces || 0,
        last14DaysBounces: client.last_14_days_bounces || 0,
        last30DaysBounces: client.last_30_days_bounces || 0,
      }));

      console.log('📊 [useClientSpecificEmailAnalytics] Transformed client analytics:', clientAnalytics);

      // Calculate and log totals for verification against main analytics
      const totals = {
        sent: clientAnalytics.reduce((sum, client) => sum + client.last30Days, 0),
        replies: clientAnalytics.reduce((sum, client) => sum + client.last30DaysReplies, 0),
        bounces: clientAnalytics.reduce((sum, client) => sum + client.last30DaysBounces, 0),
      };
      console.log('📊 [useClientSpecificEmailAnalytics] Client totals (last 30 days) - Should match main analytics:', totals);

      return clientAnalytics;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
